import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop } from "../../utils";
import { saveNodeType } from "../utils";
import { getXValue } from "../../utils/ChartDataUtil";

import ClickableCircle from "../components/ClickableCircle";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

// Base dos wrappers de níveis derivados do intervalo (QuadrantLines /
// TironeLevels): 474 linhas cada, diferindo só na fórmula dos níveis
// intermediários (quartis vs terços) e nos nomes das chaves (auditoria
// 2026-07-17, item 1d). Subclasses definem o estático
// computeMidLevels({highest, lowest, avg}) -> { upper, lower } (preços).
// EachAverageLine fica FORA (geometria própria — meio-gêmeo).
class EachDerivedLevels extends Component {
  constructor(props) {
    super(props);

    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);
    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleHover = this.handleHover.bind(this);

    this.isHover = this.isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x1, x2 } = this.props;
    this.dragStart = { x1, x2 };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;
    const { x1, x2 } = this.dragStart;

    const { xScale, xAccessor, fullData } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const px1 = xScale(x1);
    const px2 = xScale(x2);

    const dx = startPos[0] - mouseXY[0];

    const newX1 = getXValue(xScale, xAccessor, [px1 - dx, 0], fullData);
    const newX2 = getXValue(xScale, xAccessor, [px2 - dx, 0], fullData);

    onDrag(index, {
      x1: newX1,
      x2: newX2,
    });
  }

  handlePoint1Drag(moreProps) {
    const { index, onDrag, x2 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX1 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1: newX1,
      x2,
    });
  }

  handlePoint2Drag(moreProps) {
    const { index, onDrag, x1 } = this.props;
    const { xScale, xAccessor, fullData, mouseXY } = moreProps;

    const newX2 = getXValue(xScale, xAccessor, mouseXY, fullData);

    onDrag(index, {
      x1,
      x2: newX2,
    });
  }

  helper(props, moreProps) {
    const { x1, x2 } = props;
    const { xScale, chartConfig, fullData } = moreProps;
    const { yScale } = chartConfig;

    // Convert time coordinates to pixel coordinates
    const px1 = xScale(x1);
    const px2 = xScale(x2);

    // Find highest/lowest Close prices in range
    const start = Math.min(x1, x2);
    const end = Math.max(x1, x2);

    let highest = null;
    let lowest = null;
    let validTicks = 0;

    for (let i = start; i <= end; i++) {
      if (!fullData[i]) continue;

      const price = fullData[i].close;
      if (price || price === 0) {
        validTicks++;
        if (highest === null || price > highest) highest = price;
        if (lowest === null || price < lowest) lowest = price;
      }
    }

    // Calculate derived levels
    let levels = null;
    if (validTicks > 0 && highest !== null && lowest !== null) {
      const avg = (highest + lowest) / 2;
      const mid = this.constructor.computeMidLevels({ highest, lowest, avg });
      levels = {
        p100: yScale(highest), // Top boundary (100%)
        upper: yScale(mid.upper), // Nível intermediário superior
        p50: yScale(avg), // Median (50%)
        lower: yScale(mid.lower), // Nível intermediário inferior
        p0: yScale(lowest), // Bottom boundary (0%)
      };
    }

    return {
      px1,
      px2,
      levels,
      validTicks,
      highest,
      lowest,
    };
  }

  isHover(moreProps) {
    const { mouseXY } = moreProps;
    const [mx, my] = mouseXY;

    const { px1, px2, levels } = this.helper(this.props, moreProps);

    if (!levels) return false;

    const tolerance = 10;

    // Check distance to anchor points (on median line)
    const distToP1 = Math.sqrt(
      Math.pow(mx - px1, 2) + Math.pow(my - levels.p50, 2),
    );
    const distToP2 = Math.sqrt(
      Math.pow(mx - px2, 2) + Math.pow(my - levels.p50, 2),
    );

    if (distToP1 < tolerance || distToP2 < tolerance) {
      return true;
    }

    const minX = Math.min(px1, px2);
    const minY = Math.min(levels.p0, levels.p100);
    const maxX = Math.max(px1, px2);
    const maxY = Math.max(levels.p0, levels.p100);

    // Check if mouse is near any of the 5 horizontal lines
    const horizontalLines = [
      levels.p100,
      levels.upper,
      levels.p50,
      levels.lower,
      levels.p0,
    ];
    for (const yLine of horizontalLines) {
      if (mx >= minX && mx <= maxX && Math.abs(my - yLine) < tolerance) {
        return true;
      }
    }

    // Check if mouse is near vertical end markers
    if (
      (Math.abs(mx - px1) < tolerance && my >= minY && my <= maxY) ||
      (Math.abs(mx - px2) < tolerance && my >= minY && my <= maxY)
    ) {
      return true;
    }

    return false;
  }

  renderSVG(moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#2196F3";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray =
      appearance.stroke?.dash === "Solid" ? "none" : "5,5";

    const { px1, px2, levels, validTicks } = this.helper(this.props, moreProps);

    // Don't render if no valid data
    if (validTicks === 0 || !levels) {
      return null;
    }

    return (
      <g>
        {/* 5 Horizontal lines */}
        <line
          x1={px1}
          y1={levels.p100}
          x2={px2}
          y2={levels.p100}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />
        <line
          x1={px1}
          y1={levels.upper}
          x2={px2}
          y2={levels.upper}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />
        <line
          x1={px1}
          y1={levels.p50}
          x2={px2}
          y2={levels.p50}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />
        <line
          x1={px1}
          y1={levels.lower}
          x2={px2}
          y2={levels.lower}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />
        <line
          x1={px1}
          y1={levels.p0}
          x2={px2}
          y2={levels.p0}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* Vertical end markers */}
        <line
          x1={px1}
          y1={levels.p0}
          x2={px1}
          y2={levels.p100}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />
        <line
          x1={px2}
          y1={levels.p0}
          x2={px2}
          y2={levels.p100}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* Drag handles at median line */}
        {(selected || hover) && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={px1}
              cy={levels.p50}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={this.props.onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={px2}
              cy={levels.p50}
              r={6}
              fill={stroke}
              stroke="#FFFFFF"
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={this.props.onDragComplete}
            />
          </g>
        )}
      </g>
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { appearance, selected } = this.props;
    const { hover } = this.state;

    const stroke = appearance.stroke?.color || "#2196F3";
    const strokeWidth = appearance.stroke?.width || 1;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash === "Solid" ? [] : [5, 5];

    const { px1, px2, levels, validTicks } = this.helper(this.props, moreProps);

    // Don't render if no valid data
    if (validTicks === 0 || !levels) {
      return;
    }

    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.globalAlpha = strokeOpacity;

    if (strokeDasharray.length > 0) {
      ctx.setLineDash(strokeDasharray);
    } else {
      ctx.setLineDash([]);
    }

    // Draw 5 horizontal lines
    const horizontalLines = [
      levels.p100,
      levels.upper,
      levels.p50,
      levels.lower,
      levels.p0,
    ];
    horizontalLines.forEach((yLine) => {
      ctx.beginPath();
      ctx.moveTo(px1, yLine);
      ctx.lineTo(px2, yLine);
      ctx.stroke();
    });

    // Draw 2 vertical end markers
    ctx.beginPath();
    ctx.moveTo(px1, levels.p0);
    ctx.lineTo(px1, levels.p100);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(px2, levels.p0);
    ctx.lineTo(px2, levels.p100);
    ctx.stroke();

    // Draw drag handles if selected or hovered
    if (selected || hover) {
      ctx.globalAlpha = 1;
      ctx.setLineDash([]);

      // Draw point 1 at median
      ctx.fillStyle = stroke;
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(px1, levels.p50, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      // Draw point 2 at median
      ctx.beginPath();
      ctx.arc(px2, levels.p50, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // Reset context
    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  }

  render() {
    const { x2, hoverText, selected } = this.props;
    const { hover } = this.state;

    if (x2 === null || x2 === undefined) {
      return null;
    }

    const {
      enable: hoverTextEnabled,
      // selectedText é destruturado só p/ ficar FORA do restHoverTextProps
      // eslint-disable-next-line no-unused-vars
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    return (
      <g>
        <GenericChartComponent
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineMove}
          onDragComplete={this.props.onDragComplete}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          interactiveCursorClass="react-stockcharts-move-cursor"
          selected={selected}
          enableDragOnHover={true}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

export const derivedLevelsWrapperPropTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  x2: PropTypes.number,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

export const derivedLevelsWrapperDefaults = {
  onDrag: noop,
  onDragComplete: noop,
  selected: false,

  hoverText: {
    enable: false,
  },
};

EachDerivedLevels.propTypes = derivedLevelsWrapperPropTypes;
EachDerivedLevels.defaultProps = derivedLevelsWrapperDefaults;

export default EachDerivedLevels;
